import React, { useState, useMemo } from "react";
import { Modal, Form, Input, Select, Button, DatePicker, Card, Typography, Result, Row, Col, InputNumber, Flex, message } from "antd";
import { UserOutlined, BankOutlined, EnvironmentOutlined, MailOutlined, LockOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { registerUser } from "../../../services/authService";
import logoImg from "../../assets/logo/dealVoice.png";
import heroImg from "../../assets/landing-page/hero page.png";

const { Title, Text, Paragraph } = Typography;

const COUNTRY_OPTIONS = [
  { value: "+91", label: "🇮🇳 +91" },
  { value: "+1", label: "🇺🇸 +1" },
  { value: "+44", label: "🇬🇧 +44" },
  { value: "+61", label: "🇦🇺 +61" },
  { value: "+971", label: "🇦🇪 +971" },
];

const INDUSTRIES = ["Technology & Software", "Real Estate & Property", "Financial Services & Banking", "Healthcare & Pharma", "Manufacturing & Industrial", "Education & E-Learning", "Retail & E-commerce", "Consulting & Professional Services", "Logistics & Supply Chain", "Other"];

const REQUIREMENT_CONFIG = [
  { key: "length", label: "8+ characters" },
  { key: "upper", label: "Uppercase letter" },
  { key: "number", label: "Number" },
  { key: "symbol", label: "Symbol (!@#…)" },
];

const checkPassword = (password = "") => {
  const length = password.length >= 8;
  const upper = /[A-Z]/.test(password);
  const number = /\d/.test(password);
  const symbol = /[^A-Za-z\d]/.test(password);
  const isStrong = length && upper && number && symbol;
  return { length, upper, number, symbol, isStrong };
};

const calculateCostPreview = (isTrialRequest, paymentToDate, teamSize) => {
  if (isTrialRequest || !paymentToDate) return null;
  const now = dayjs();
  const days = Math.max(1, paymentToDate.diff(now, "day"));
  const base = Number(teamSize || 1) * 10 * days;
  const total = Math.round(base * 1.18);
  return {
    amountRupees: total,
    teamSizeMax: teamSize,
    days,
  };
};

const PasswordChecklist = ({ checks }) => (
  <Row gutter={[8, 4]} style={{ marginTop: 8 }}>
    {REQUIREMENT_CONFIG.map(({ key, label }) => {
      const isValid = Boolean(checks[key]);
      return (
        <Col span={12} key={key}>
          <Text style={{ fontSize: 12, color: isValid ? "#10b981" : "#94a3b8" }}>
            {isValid ? "✓" : "○"} {label}
          </Text>
        </Col>
      );
    })}
  </Row>
);

const CostPreviewCard = ({ costPreview }) => {
  if (!costPreview) return null;
  return (
    <Card size="small" style={{ marginTop: 12, background: "#f8fafc", borderColor: "#e2e8f0" }}>
      <Text type="secondary" style={{ fontSize: 12 }}>
        Total (Incl. 18% GST)
      </Text>
      <Flex align="baseline" gap={16} wrap="wrap">
        <Title level={3} style={{ color: "#2459d3", margin: 0 }}>
          ₹{costPreview.amountRupees.toLocaleString()}
        </Title>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {costPreview.teamSizeMax} people × ₹10/day × {costPreview.days} days
        </Text>
      </Flex>
    </Card>
  );
};

const SignupSuccessView = ({ isTrialRequest, onProceed }) => (
  <Result
    status="success"
    title={isTrialRequest ? "Request Sent!" : "Account Created!"}
    subTitle={isTrialRequest ? "Your trial request has been sent to the main admin for approval. You will receive an email once approved." : "Welcome to DealVoice. You can now log in with your credentials."}
    extra={[
      <Button type="primary" key="login" size="large" onClick={onProceed}>
        {isTrialRequest ? "Got it" : "Go to Log In"}
      </Button>,
    ]}
  />
);

const LeftHeroSection = () => (
  <Col
    xs={0}
    md={10}
    lg={12}
    style={{
      background: "#f8f9fa",
      padding: "2.5rem 3rem",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      borderRadius: 16,
      height: "100%",
      overflow: "hidden",
    }}
  >
    <Title
      level={1}
      style={{
        fontSize: "2.5rem",
        lineHeight: 1.15,
        marginBottom: "0.75rem",
        color: "#0f172a",
        letterSpacing: "-0.03em",
      }}
    >
      <span style={{ color: "#8d8d8d", fontWeight: 500 }}>The Intelligent CRM for</span>
      <br />
      sales deal closing.
    </Title>
    <Paragraph style={{ fontSize: "0.95rem", color: "#64748b", marginBottom: "1.25rem", maxWidth: 520 }}>DealVoice automates the heavy lifting of sales pipeline management. Live in hours. Learning from every deal.</Paragraph>
    <img
      src={heroImg}
      alt="Dashboard Preview"
      style={{
        width: "100%",
        maxWidth: "100%",
        borderRadius: 14,
        boxShadow: "0 15px 35px rgba(0,0,0,0.08)",
        objectFit: "contain",
      }}
    />
  </Col>
);

function SignupModal({ isOpen, isTrialRequest, initialInput = "", closeModals, openLogin }) {
  const [form] = Form.useForm();
  const [password, setPassword] = useState("");
  const [teamSize, setTeamSize] = useState(5);
  const [paymentToDate, setPaymentToDate] = useState(dayjs().add(30, "day"));
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  React.useEffect(() => {
    if (isOpen && initialInput) {
      if (initialInput.includes("@")) {
        form.setFieldsValue({ email: initialInput });
      } else {
        form.setFieldsValue({ companyName: initialInput });
      }
    }
  }, [isOpen, initialInput, form]);

  const pwdChecks = useMemo(() => checkPassword(password), [password]);
  const costPreview = useMemo(() => calculateCostPreview(isTrialRequest, paymentToDate, teamSize), [isTrialRequest, paymentToDate, teamSize]);

  const onFinish = async (values) => {
    if (!pwdChecks.isStrong) {
      message.error("Please ensure password meets all security requirements.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        companyName: values.companyName?.trim(),
        name: values.name?.trim(),
        companyAddress: values.companyAddress?.trim(),
        email: values.email?.trim().toLowerCase(),
        countryCode: values.countryCode || "+91",
        mobile: values.mobile?.trim(),
        teamSize: Number(values.teamSize || teamSize || 5),
        industry: values.industry,
        password: values.password,
        isTrial: Boolean(isTrialRequest),
      };

      await registerUser(payload);
      setSuccess(true);
      form.resetFields();
      setPassword("");
    } catch (err) {
      message.error(err.message || "Failed to submit registration. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessProceed = () => {
    setSuccess(false);
    closeModals();
    if (!isTrialRequest) openLogin();
  };

  return (
    <Modal
      title={null}
      footer={null}
      onCancel={closeModals}
      open={isOpen}
      width={1250}
      centered
      closable={true}
      styles={{
        body: {
          padding: 0,
          overflow: "hidden",
          borderRadius: 16,
          height: "75vh",
          maxHeight: "75vh",
        },
      }}
    >
      <Row style={{ height: "75vh", maxHeight: "75vh", margin: 0 }}>
        {/* Left Side - Hero Content */}
        <LeftHeroSection />

        {/* Right Side - Form */}
        <Col
          xs={24}
          md={14}
          lg={12}
          className="hide-scrollbar"
          style={{
            padding: "1.75rem 2.5rem",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            background: "#fff",
            height: "100%",
            overflowY: "auto",
            scrollbarWidth: "none",
            borderTopRightRadius: 16,
            borderBottomRightRadius: 16,
          }}
        >
          <div style={{ maxWidth: 480, width: "100%", margin: "0 auto", padding: "0.5rem 0" }}>
            <div style={{ marginBottom: "1.5rem" }}>
              <img src={logoImg} alt="DealVoice Logo" style={{ height: 32, objectFit: "contain" }} />
            </div>
            <Title level={2} style={{ margin: "0 0 0.5rem" }}>
              {isTrialRequest ? "Request Free Demo" : "Create your account"}
            </Title>
            <Paragraph type="secondary" style={{ marginBottom: "2rem" }}>
              {isTrialRequest ? "Get 7 days of full access to DealVoice features" : "Start tracking calls for free — no credit card required"}
            </Paragraph>

            {success ? (
              <SignupSuccessView isTrialRequest={isTrialRequest} onProceed={handleSuccessProceed} />
            ) : (
              <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                initialValues={{
                  countryCode: "+91",
                  teamSize: 5,
                  subscriptionDate: dayjs().add(30, "day"),
                }}
                requiredMark="optional"
              >
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="Company Name" name="companyName" rules={[{ required: true, message: "Required" }]}>
                      <Input size="large" prefix={<BankOutlined style={{ color: "#94a3b8" }} />} placeholder="Softrate Global" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Your Name" name="name" rules={[{ required: true, message: "Required" }]}>
                      <Input size="large" prefix={<UserOutlined style={{ color: "#94a3b8" }} />} placeholder="Franklin" />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item label="Company Address" name="companyAddress" rules={[{ required: true, message: "Required" }]}>
                  <Input size="large" prefix={<EnvironmentOutlined style={{ color: "#94a3b8" }} />} placeholder="123 Industry Park, Tech City" />
                </Form.Item>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      label="Work Email"
                      name="email"
                      rules={[
                        { required: true, message: "Required" },
                        { type: "email", message: "Invalid email" },
                      ]}
                    >
                      <Input size="large" prefix={<MailOutlined style={{ color: "#94a3b8" }} />} placeholder="you@company.com" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Mobile Number" name="mobile" rules={[{ required: true, message: "Required" }]}>
                      <Input
                        size="large"
                        addonBefore={
                          <Form.Item name="countryCode" noStyle>
                            <Select style={{ width: 85 }} options={COUNTRY_OPTIONS} />
                          </Form.Item>
                        }
                        placeholder="9876543210"
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="Team Capacity Limit" name="teamSize" rules={[{ required: true, message: "Required" }]} extra="Employee creation limit for your organization.">
                      <InputNumber size="large" min={1} style={{ width: "100%" }} onChange={(val) => setTeamSize(val || 1)} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Industry" name="industry" rules={[{ required: true, message: "Required" }]}>
                      <Select size="large" placeholder="Select Industry" options={INDUSTRIES.map((ind) => ({ value: ind, label: ind }))} />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  label="Password"
                  name="password"
                  rules={[
                    { required: true, message: "Required" },
                    {
                      validator: (_, value) => {
                        if (!value || checkPassword(value).isStrong) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error("Password does not meet security requirements"));
                      },
                    },
                  ]}
                  style={{ marginBottom: 4 }}
                >
                  <Input.Password size="large" prefix={<LockOutlined style={{ color: "#94a3b8" }} />} placeholder="Min. 8 chars + A + 1 + !" onChange={(e) => setPassword(e.target.value)} />
                </Form.Item>
                <div style={{ marginBottom: 16 }}>
                  <PasswordChecklist checks={pwdChecks} />
                </div>

                {!isTrialRequest && (
                  <Form.Item label="Subscription End Date" name="subscriptionDate" rules={[{ required: true, message: "Required" }]}>
                    <DatePicker size="large" style={{ width: "100%" }} disabledDate={(current) => current && current < dayjs().endOf("day")} onChange={(date) => setPaymentToDate(date)} />
                    <CostPreviewCard costPreview={costPreview} />
                  </Form.Item>
                )}

                <Form.Item style={{ marginTop: 24 }}>
                  <Button type="primary" htmlType="submit" size="large" block loading={loading} disabled={!pwdChecks.isStrong} style={{ height: 44, borderRadius: 12, fontWeight: 600 }}>
                    {isTrialRequest ? "Request Trial →" : "Create & Pay →"}
                  </Button>
                </Form.Item>
              </Form>
            )}

            {!success && (
              <div style={{ textAlign: "center" }}>
                <Text type="secondary">
                  Already have an account?{" "}
                  <Button
                    type="link"
                    style={{ padding: 0, fontWeight: 600 }}
                    onClick={(e) => {
                      e.preventDefault();
                      openLogin();
                    }}
                  >
                    Log In
                  </Button>
                </Text>
              </div>
            )}
          </div>
        </Col>
      </Row>
    </Modal>
  );
}

export default SignupModal;
